import React, { useState, useEffect, useCallback } from 'react';
import { booksApi, getThumbnailUrl, getImageUrl } from '../services/api';
import { Book } from '../types';
import './BookList.css';

interface BookListProps {
  onEdit?: (book: Book) => void;
  onRefresh?: () => void;
}

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function ConfirmModal({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmModalProps): React.ReactElement | null {
  if (!isOpen) return null;

  return (
    <div className="book-list__modal-overlay" onClick={onCancel}>
      <div
        className="book-list__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <h3 id="modal-title" className="book-list__modal-title">
          {title}
        </h3>
        <p className="book-list__modal-message">{message}</p>
        <div className="book-list__modal-actions">
          <button
            type="button"
            className="book-list__button book-list__button--secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="book-list__button book-list__button--danger"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Removendo...' : 'Confirmar Remoção'}
          </button>
        </div>
      </div>
    </div>
  );
}


export function BookList({ onEdit, onRefresh }: BookListProps): React.ReactElement {
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [bookToDelete, setBookToDelete] = useState<Book | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const loadBooks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await booksApi.getAll();
      setBooks(data);
    } catch (err) {
      const errorMessage =
        (err as { message?: string })?.message || 'Erro ao carregar projetos. Tente novamente.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBooks();
  }, [loadBooks]);

  // Limpa mensagem de sucesso após 3 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleDeleteClick = (book: Book) => {
    setBookToDelete(book);
  };

  const handleCancelDelete = () => {
    setBookToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!bookToDelete) return;

    setIsDeleting(true);
    try {
      await booksApi.delete(bookToDelete.id);
      setBooks((prev) => prev.filter((b) => b.id !== bookToDelete.id));
      setSuccessMessage(`Projeto "${bookToDelete.title}" removido com sucesso!`);
      setBookToDelete(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      const errorMessage =
        (err as { message?: string })?.message || 'Erro ao remover projeto. Tente novamente.';
      setError(errorMessage);
      setBookToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = (book: Book) => {
    if (onEdit) {
      onEdit(book);
    }
  };

  const getBookThumbnail = (book: Book): string | null => {
    if (book.images && book.images.length > 0) {
      const firstImage = book.images[0];
      return getThumbnailUrl(firstImage.filepath);
    }
    return null;
  };

  const getBookImageFallback = (book: Book): string | null => {
    if (book.images && book.images.length > 0) {
      return getImageUrl(book.images[0].filepath);
    }
    return null;
  };

  const formatTags = (book: Book): string => {
    if (!book.tags || book.tags.length === 0) return 'Sem tags';
    return book.tags.map((t) => t.name).join(', ');
  };

  const filteredBooks = books.filter(book => {
    if (!searchFilter.trim()) return true;
    const search = searchFilter.toLowerCase();
    return book.title.toLowerCase().includes(search) || 
           book.description.toLowerCase().includes(search) ||
           book.tags?.some(t => t.name.toLowerCase().includes(search));
  });

  if (isLoading) {
    return (
      <div className="book-list">
        <div className="book-list__loading">
          <div className="book-list__spinner" />
          <span>Carregando projetos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="book-list">
      <div className="book-list__header">
        <h2 className="book-list__title">Lista de Projetos</h2>
        <div className="book-list__header-actions">
          <input
            type="text"
            placeholder="Buscar projetos..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="book-list__search-input"
          />
          <button
            type="button"
            className="book-list__button book-list__button--secondary"
            onClick={loadBooks}
            disabled={isLoading}
          >
            Atualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="book-list__message book-list__message--error">
          {error}
          <button
            type="button"
            className="book-list__message-close"
            onClick={() => setError(null)}
            aria-label="Fechar mensagem"
          >
            ✕
          </button>
        </div>
      )}

      {successMessage && (
        <div className="book-list__message book-list__message--success">
          {successMessage}
          <button
            type="button"
            className="book-list__message-close"
            onClick={() => setSuccessMessage(null)}
            aria-label="Fechar mensagem"
          >
            ✕
          </button>
        </div>
      )}

      {filteredBooks.length === 0 ? (
        <div className="book-list__empty">
          <p>{searchFilter ? 'Nenhum projeto encontrado com esse filtro.' : 'Nenhum projeto cadastrado.'}</p>
          <p className="book-list__empty-hint">
            {searchFilter ? 'Tente outro termo de busca.' : 'Use o formulário acima para cadastrar seu primeiro projeto.'}
          </p>
        </div>
      ) : (
        <div className="book-list__table-container">
          <table className="book-list__table">
            <thead>
              <tr>
                <th className="book-list__th book-list__th--image">Imagem</th>
                <th className="book-list__th">Título</th>
                <th className="book-list__th book-list__th--year">Ano</th>
                <th className="book-list__th book-list__th--available">Disponível</th>
                <th className="book-list__th book-list__th--awarded">Premiado</th>
                <th className="book-list__th book-list__th--tags">Tags</th>
                <th className="book-list__th book-list__th--actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredBooks.map((book) => (
                <tr key={book.id} className="book-list__row">
                  <td className="book-list__td book-list__td--image">
                    {getBookThumbnail(book) ? (
                      <img
                        src={getBookThumbnail(book)!}
                        alt={`Capa de ${book.title}`}
                        className="book-list__thumbnail"
                        onError={(e) => {
                          const fallback = getBookImageFallback(book);
                          if (fallback) {
                            (e.target as HTMLImageElement).src = fallback;
                          }
                        }}
                      />
                    ) : (
                      <div className="book-list__no-image">
                        <span>Sem imagem</span>
                      </div>
                    )}
                  </td>
                  <td className="book-list__td book-list__td--title">
                    <span className="book-list__book-title">{book.title}</span>
                    <span className="book-list__book-description">
                      {book.description.length > 100
                        ? `${book.description.substring(0, 100)}...`
                        : book.description}
                    </span>
                  </td>
                  <td className="book-list__td book-list__td--year">
                    {book.publicationYear || '-'}
                  </td>
                  <td className="book-list__td book-list__td--available">
                    <span
                      className={`book-list__badge ${
                        book.physicallyAvailable
                          ? 'book-list__badge--available'
                          : 'book-list__badge--unavailable'
                      }`}
                    >
                      {book.physicallyAvailable ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td className="book-list__td book-list__td--awarded">
                    <span
                      className={`book-list__badge ${
                        book.isAwarded
                          ? 'book-list__badge--awarded'
                          : 'book-list__badge--not-awarded'
                      }`}
                    >
                      {book.isAwarded ? '🏆 Sim' : 'Não'}
                    </span>
                  </td>
                  <td className="book-list__td book-list__td--tags">
                    <span className="book-list__tags-text">{formatTags(book)}</span>
                  </td>
                  <td className="book-list__td book-list__td--actions">
                    <div className="book-list__actions">
                      <button
                        type="button"
                        className="book-list__action-btn book-list__action-btn--edit"
                        onClick={() => handleEditClick(book)}
                        title="Editar projeto"
                        aria-label={`Editar ${book.title}`}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="book-list__action-btn book-list__action-btn--delete"
                        onClick={() => handleDeleteClick(book)}
                        title="Remover projeto"
                        aria-label={`Remover ${book.title}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={bookToDelete !== null}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover o projeto "${bookToDelete?.title}"? Esta ação não pode ser desfeita e todas as imagens associadas serão excluídas.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}

export default BookList;
